import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../hooks/useLanguage';
import { motion } from 'framer-motion';

// Contact information data
const contactInfo = {
  phone: {
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
      />
    ),
    value: '+91 95409 36061'
  },
  email: {
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    ),
    value: 'rksstrust00@gmail.com'
  },
  address: {
    icon: (
      <>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </>
    ),
    value: 'Village – Chheet Ka Purwa, Post – Gauri, District – Fatehpur (Uttar Pradesh), India'
  }
};

// Translations
const translations = {
  en: {
    title: 'Get in Touch',
    subtitle: 'We\'d Love to Hear From You',
    description: 'Have questions about our services or want to know how you can contribute? Reach out to us!',
    contactButton: 'Contact Us Now',
    phone: 'Phone',
    email: 'Email',
    address: 'Address'
  },
  hi: {
    title: 'संपर्क करें',
    subtitle: 'हम आपसे सुनना चाहेंगे',
    description: 'हमारी सेवाओं के बारे में प्रश्न हैं या जानना चाहते हैं कि आप कैसे योगदान दे सकते हैं? हमसे संपर्क करें!',
    contactButton: 'अभी संपर्क करें',
    phone: 'फोन',
    email: 'ईमेल',
    address: 'पता'
  }
};

// Animation variants
const animations = {
  container: {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2
      }
    }
  },
  item: {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  },
  decorativeCircle: {
    animate: {
      scale: [1, 1.1, 1],
      rotate: [0, 5, -5, 0]
    },
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
};

// Contact Info Item Component
const ContactInfoItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
}> = ({ icon, label, value }) => (
  <div className="flex items-center space-x-4">
    <div className="bg-primary/10 p-3 rounded-full">
      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        {icon}
      </svg>
    </div>
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="font-medium">{value}</p>
    </div>
  </div>
);

const ContactUsTile: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];

  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container-custom">
        <motion.div
          variants={animations.container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Content Section */}
            <div className="p-8 lg:p-12">
              <motion.div variants={animations.item} className="space-y-6">
                <h2 className="text-4xl font-bold text-gray-900">{t.title}</h2>
                <h3 className="text-2xl text-primary">{t.subtitle}</h3>
                <p className="text-gray-600 text-lg">{t.description}</p>

                <div className="space-y-4">
                  <ContactInfoItem
                    icon={contactInfo.phone.icon}
                    label={t.phone}
                    value={contactInfo.phone.value}
                  />
                  <ContactInfoItem
                    icon={contactInfo.email.icon}
                    label={t.email}
                    value={contactInfo.email.value}
                  />
                  <ContactInfoItem
                    icon={contactInfo.address.icon}
                    label={t.address}
                    value={contactInfo.address.value}
                  />
                </div>

                <Link to="/contact" className="inline-block mt-8">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-8 py-4 bg-gradient-to-r from-primary to-secondary text-white rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    {t.contactButton}
                  </motion.button>
                </Link>
              </motion.div>
            </div>

            {/* Decorative Section */}
            <div className="hidden lg:block relative bg-gradient-to-br from-primary/10 to-secondary/10">
              <div className="absolute inset-0 bg-[url('https://placehold.co/800x600')] bg-cover bg-center mix-blend-overlay opacity-20" />
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20" />
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  {...animations.decorativeCircle}
                  className="w-64 h-64 bg-white/10 rounded-full backdrop-blur-sm"
                />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactUsTile; 